import { useEffect, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import {
  DEFAULT_EDITOR_SETTINGS,
  FORMAT_SHORTCUT_OPTIONS,
  normalizeFormatShortcut,
} from '../editor/codemirrorConfig';
import {
  DEFAULT_MAX_OUTPUT_LINES,
  MIN_MAX_OUTPUT_LINES,
  MAX_MAX_OUTPUT_LINES,
  clampMaxOutputLines,
} from '../utils/outputLimits';

const AUTOCOMPLETE_MODES = ['off', 'manual', 'typing'];

function mergeSettings(fromProfile) {
  const base = { ...DEFAULT_EDITOR_SETTINGS, taskTextWrapping: true };
  if (!fromProfile || typeof fromProfile !== 'object') return base;
  const merged = { ...base, ...fromProfile };
  if (!AUTOCOMPLETE_MODES.includes(merged.autocompleteMode)) {
    merged.autocompleteMode = DEFAULT_EDITOR_SETTINGS.autocompleteMode;
  }
  merged.formatShortcut = normalizeFormatShortcut(merged.formatShortcut);
  const statementScale = Number(merged.workStatementFontScale);
  const sidebarScale = Number(merged.workSidebarFontScale);
  merged.workStatementFontScale = Number.isFinite(statementScale) ? statementScale : 1;
  merged.workSidebarFontScale = Number.isFinite(sidebarScale) ? sidebarScale : 1;
  return merged;
}

export default function EditorSettingsPage() {
  const { user, refreshUser } = useAuth();
  const isSiteAdmin = Boolean(user?.is_staff || user?.is_admin);
  const [settings, setSettings] = useState(() => mergeSettings(user?.editor_settings));
  const [maxOutputLines, setMaxOutputLines] = useState(DEFAULT_MAX_OUTPUT_LINES);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setSettings(mergeSettings(user?.editor_settings));
  }, [user?.editor_settings]);

  useEffect(() => {
    if (!isSiteAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/auth/site-settings/');
        if (!cancelled) {
          setMaxOutputLines(clampMaxOutputLines(res.data?.max_output_lines));
        }
      } catch {
        // оставляем дефолт
      }
    })();
    return () => { cancelled = true; };
  }, [isSiteAdmin]);

  function updateField(field, value) {
    setSettings((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.patch('/auth/profile/', { editor_settings: settings });
      if (isSiteAdmin) {
        const lines = clampMaxOutputLines(maxOutputLines);
        const res = await api.patch('/auth/site-settings/', { max_output_lines: lines });
        setMaxOutputLines(clampMaxOutputLines(res.data?.max_output_lines ?? lines));
      }
      await refreshUser?.();
      setSuccess('Настройки сохранены');
    } catch (e2) {
      const msg = e2?.response?.data;
      const fieldErr = msg?.max_output_lines;
      setError(
        typeof msg === 'string'
          ? msg
          : (Array.isArray(fieldErr) ? fieldErr[0] : null)
            || msg?.detail
            || 'Не удалось сохранить настройки',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>Настройки редактора</h1>
        <p className="page-subtitle">Применяются в редакторах кода и тексте задачи.</p>
      </div>

      <form className="profile-form" onSubmit={handleSave}>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="form-grid">
          <div className="form-group">
            <label>Размер текста (px)</label>
            <input
              type="number"
              className="form-input"
              min={10}
              max={24}
              value={settings.fontSizePx}
              onChange={(e) => updateField('fontSizePx', e.target.value ? Number(e.target.value) : DEFAULT_EDITOR_SETTINGS.fontSizePx)}
            />
          </div>

          <div className="form-group">
            <label>Интерлиньяж</label>
            <input
              type="number"
              className="form-input"
              min={1.1}
              max={2.2}
              step={0.05}
              value={settings.lineHeight}
              onChange={(e) => updateField('lineHeight', e.target.value ? Number(e.target.value) : DEFAULT_EDITOR_SETTINGS.lineHeight)}
            />
          </div>

          <div className="form-group">
            <label>Размер текста условия</label>
            <input
              type="number"
              className="form-input"
              min={0.8}
              max={1.4}
              step={0.05}
              value={settings.workStatementFontScale ?? 1}
              onChange={(e) => updateField('workStatementFontScale', e.target.value ? Number(e.target.value) : 1)}
            />
          </div>

          <div className="form-group">
            <label>Размер текста сайдбара</label>
            <input
              type="number"
              className="form-input"
              min={0.75}
              max={1.3}
              step={0.05}
              value={settings.workSidebarFontScale ?? 1}
              onChange={(e) => updateField('workSidebarFontScale', e.target.value ? Number(e.target.value) : 1)}
            />
          </div>

          <div className="form-group">
            <label>Автодополнение</label>
            <select
              className="form-select"
              value={AUTOCOMPLETE_MODES.includes(settings.autocompleteMode) ? settings.autocompleteMode : 'off'}
              onChange={(e) => updateField('autocompleteMode', e.target.value)}
            >
              <option value="off">Выключено</option>
              <option value="manual">Только по Ctrl+Space</option>
              <option value="typing">Автоматически при наборе</option>
            </select>
            <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.35rem', marginBottom: 0 }}>
              Всплывающий список шаблонов кода (например «if block») при наборе — это автодополнение, не подсказки к задаче.
            </p>
          </div>

          <div className="form-group">
            <label>Сочетание «формат»</label>
            <select
              className="form-select"
              value={
                FORMAT_SHORTCUT_OPTIONS.includes(settings.formatShortcut)
                  ? settings.formatShortcut
                  : DEFAULT_EDITOR_SETTINGS.formatShortcut
              }
              onChange={(e) => updateField('formatShortcut', e.target.value)}
            >
              <option value="ctrl-shift-f">Ctrl+Shift+F — если язык переключается через Alt+Shift</option>
              <option value="shift-alt-f">Shift+Alt+F (как в VS Code) — если язык через Ctrl+Shift</option>
              <option value="off">Отключить — только кнопка</option>
            </select>
            <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.35rem', marginBottom: 0 }}>
              Расставляет пробелы и отступы в редакторе. Работает и на русской раскладке.
            </p>
          </div>

          {isSiteAdmin && (
            <div className="form-group">
              <label>Лимит строк вывода (весь сайт)</label>
              <input
                type="number"
                className="form-input"
                min={MIN_MAX_OUTPUT_LINES}
                max={MAX_MAX_OUTPUT_LINES}
                step={100}
                value={maxOutputLines}
                onChange={(e) => setMaxOutputLines(
                  e.target.value ? Number(e.target.value) : DEFAULT_MAX_OUTPUT_LINES,
                )}
              />
              <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.35rem', marginBottom: 0 }}>
                Сколько последних строк вывода оставлять после запуска программы.
                Действует для всех пользователей со следующего запуска.
                Допустимо {MIN_MAX_OUTPUT_LINES}–{MAX_MAX_OUTPUT_LINES}.
              </p>
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Опции</label>
          <div className="checkbox-row">
            <label><input type="checkbox" checked={!!settings.closeBrackets} onChange={(e) => updateField('closeBrackets', e.target.checked)} /> Автозакрытие скобок</label>
            <label><input type="checkbox" checked={!!settings.bracketMatching} onChange={(e) => updateField('bracketMatching', e.target.checked)} /> Подсветка парных скобок</label>
            <label><input type="checkbox" checked={!!settings.showHints} onChange={(e) => updateField('showHints', e.target.checked)} /> Подсказки к задаче (текст от автора)</label>
            <label><input type="checkbox" checked={!!settings.lineWrapping} onChange={(e) => updateField('lineWrapping', e.target.checked)} /> Перенос длинных строк</label>
            <label><input type="checkbox" checked={settings.taskTextWrapping !== false} onChange={(e) => updateField('taskTextWrapping', e.target.checked)} /> Перенос текста задачи</label>
            <label><input type="checkbox" checked={!!settings.lineNumbers} onChange={(e) => updateField('lineNumbers', e.target.checked)} /> Номера строк</label>
            <label><input type="checkbox" checked={!!settings.showSearch} onChange={(e) => updateField('showSearch', e.target.checked)} /> Поиск/замена (Ctrl+F / Ctrl+H)</label>
            <label><input type="checkbox" checked={!!settings.highlightSelectionMatches} onChange={(e) => updateField('highlightSelectionMatches', e.target.checked)} /> Подсветка всех вхождений</label>
            <label><input type="checkbox" checked={!!settings.highlightTrailingWhitespace} onChange={(e) => updateField('highlightTrailingWhitespace', e.target.checked)} /> Подсветка хвостовых пробелов</label>
            <label><input type="checkbox" checked={!!settings.highlightActiveLine} onChange={(e) => updateField('highlightActiveLine', e.target.checked)} /> Подсветка текущей строки</label>
            <label><input type="checkbox" checked={!!settings.rulersEnabled} onChange={(e) => updateField('rulersEnabled', e.target.checked)} /> Вертикальная линейка (80)</label>
            <label><input type="checkbox" checked={!!settings.indentGuides} onChange={(e) => updateField('indentGuides', e.target.checked)} /> Вертикальные направляющие (каждые 4 пробела)</label>
            <label><input type="checkbox" checked={settings.autosaveDrafts !== false} onChange={(e) => updateField('autosaveDrafts', e.target.checked)} /> Автосохранение черновика</label>
            <label><input type="checkbox" checked={!!settings.ligatureFont} onChange={(e) => updateField('ligatureFont', e.target.checked)} /> Шрифт с лигатурами (&lt;= и == как один символ)</label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving || !user}>
            {saving ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}
